import FallbackClassImg from "assets/fallback.png"
import moment from "moment"
import styled from "styled-components"
import { StarFullIcon } from "ui/icons/StarFullIcon.jsx"
import { fonts, themeColor, themeFontSize } from "ui/theme"
import { getAvatarUrl, getHighestDiscount, getLowestValidPrice } from "utils"
import { ClassCardSkeleton } from "./ClassCardSkeleton"

export const SearchClassCard = ({ classData, onClick, online_type }) => {
  if (!classData) return <ClassCardSkeleton />

  const highestDiscount = getHighestDiscount(classData)
  const isOnline = online_type === "online"

  const lowestPrice = isOnline
    ? classData?.price_per_online_class
    : getLowestValidPrice(
        classData?.type_of_class,
        classData?.price_per_group_class,
        classData?.price_per_private_class
      )

  const imageSrc =
    classData?.user?.gallery[0]?.name || classData?.profile_pic
      ? getAvatarUrl(
          classData?.user?.gallery[0]?.name || classData?.profile_pic
        )
      : FallbackClassImg

  return (
    <Card onClick={onClick}>
      <ImageShadowWrap>
        <ImageWrap>
          <CardImage
            src={imageSrc}
            alt={classData?.subject_sub_category || "Class"}
          />
        </ImageWrap>
      </ImageShadowWrap>

      <CardTitle>
        <CardName>{classData?.subject_sub_category}</CardName>
        <CardRating>
          <Star>
            <StarFullIcon size={12} />
          </Star>
          <RatingNum>{Number(classData?.rate || 0).toFixed(1)}</RatingNum>
        </CardRating>
      </CardTitle>

      <CardPrice>From ${lowestPrice} / person</CardPrice>

      {classData?.next_available_class?.starts_at && (
        <AvailRow>
          <CalendarIcon />
          <span>
            <AvailBold>Available:</AvailBold>{" "}
            {moment(classData.next_available_class.starts_at).format("MMM D, h:mm a")}
          </span>
        </AvailRow>
      )}

      {highestDiscount && (
        <DiscountTag>
          {highestDiscount.percentage === 100 &&
          highestDiscount.policy === "First class"
            ? "First class free!"
            : `${highestDiscount.policy} Discount: ${highestDiscount.percentage}%`}
        </DiscountTag>
      )}
    </Card>
  )
}

function CalendarIcon() {
  return (
    <svg
      width='13'
      height='20'
      viewBox='0 0 13 13'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'>
      <rect
        x='1'
        y='2'
        width='11'
        height='10'
        rx='2'
        stroke='#1d1d1f'
        strokeWidth='1.2'
      />
      <path
        d='M1 5H12'
        stroke='#1d1d1f'
        strokeWidth='1.2'
        strokeLinecap='round'
      />
      <path
        d='M4 1V3'
        stroke='#1d1d1f'
        strokeWidth='1.2'
        strokeLinecap='round'
      />
      <path
        d='M9 1V3'
        stroke='#1d1d1f'
        strokeWidth='1.2'
        strokeLinecap='round'
      />
    </svg>
  )
}

const Card = styled.article`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  cursor: pointer;
  font-family: Montserrat, sans-serif;
`

const ImageShadowWrap = styled.div`
  margin-bottom: 12px;
  width: 100%;
  border-radius: 16px;
  box-shadow: 3px 3px 9.9px rgba(0, 0, 0, 0.3);
  @media (min-width: 768px) {
    border-radius: 5px;
  }
`

const ImageWrap = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3.6;
  border-radius: 16px;
  overflow: hidden;

  @media (min-width: 768px) {
    height: 140px;
    aspect-ratio: auto;
    border-radius: 5px;
  }
`

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 5px;
  object-fit: cover;
  display: block;
  background: #0000000d;
  transition: transform 0.4s ease;
  

  ${Card}:hover & {
    transform: scale(1.04);
  }
`

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
  color: ${themeColor("txt")};

  @media (min-width: 768px) {
    transition: color 0.2s ease;

    ${Card}:hover &,
    ${Card}:focus-within & {
      color: ${themeColor("blue")};
    }
  }
`

const CardName = styled.h3`
  font-family: Poppins, sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: inherit;
  margin: 0;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;

`

const CardRating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  @media (min-width: 768px) {
    gap: 6px;
  }
`

const Star = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  color: ${themeColor("orange")};
`

const RatingNum = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${themeColor("txt")};

  @media (min-width: 768px) {
    font-size: 13px;
  }
`

const CardPrice = styled.p`
  font-size: 12px;
 font-weight: 400;
  color: #616162;
  margin: 0 0 4px 0;
  font-family: ${fonts.families.primary};
`

const AvailRow = styled.div`
  display: flex;
  gap: 5px;
  font-family: ${fonts.families.primary};
  font-size: ${themeFontSize("small")};
  font-weight: 400;
  color: #616162;
  margin: 4px 0 8px;
  @media (min-width: 768px) {
    font-size: 12px;
  }
`

const AvailBold = styled.span`
  font-family: ${fonts.families.primary};
  font-weight: 500;
  color: #1d1d1f;
`

const DiscountTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 11px;
  border-radius: 5px;
  background: #f7f5f1;
  font-family: Poppins, sans-serif;
  font-size: 11px;
  font-weight: 500;
  color: ${themeColor("blue")};
  width: fit-content;
`
